import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { APP_NAME, LOGO, applyTheme, roleTheme } from "@/lib/brand";
import {
  LayoutDashboard,
  Package,
  Users,
  Contact,
  ClipboardList,
  BarChart3,
  Plus,
  LogOut,
  Zap,
  Menu,
  CreditCard,
  Building2,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { t, tf } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { MobileMenuExtras } from "@/components/MobileMenuExtras";
import { InstallPrompt } from "@/components/InstallPrompt";
import { useFullscreen } from "@/lib/useFullscreen";
import { Maximize2, Minimize2 } from "lucide-react";

export default function AppLayout() {
  const { user, logout, companies, activeCompanyId, setActiveCompany, refreshUser } = useAuth();
  const navigate = useNavigate();
  const isCompany = user?.role === "company";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [code, setCode] = useState("");
  const fs = useFullscreen();

  const sub = user?.subscription;
  const trialBanner =
    isCompany && sub?.status === "trialing" && sub?.active && (sub?.trial_days_left ?? 99) <= 14;

  useEffect(() => {
    applyTheme(roleTheme(user, companies.find((c) => c.company_id === activeCompanyId)));
  }, [user, companies, activeCompanyId]);

  const join = useMutation({
    mutationFn: async () => (await api.post("/join-company", { invite_code: code })).data,
    onSuccess: async (d) => {
      toast.success(tf("Collegato a {company}", { company: d.company_name }));
      setJoinOpen(false);
      setCode("");
      await refreshUser();
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const nav = [
    { to: "/dashboard", label: t("Dashboard"), icon: LayoutDashboard, show: true },
    { to: "/orders/new", label: t("Nuovo ordine"), icon: Plus, show: true },
    { to: "/orders", label: t("Ordini"), icon: ClipboardList, show: true },
    { to: "/customers", label: t("Clienti"), icon: Contact, show: true },
    { to: "/products", label: t("Prodotti"), icon: Package, show: isCompany },
    { to: "/agents", label: t("Agenti"), icon: Users, show: isCompany },
    { to: "/statistics", label: t("Statistiche"), icon: BarChart3, show: true },
    { to: "/assistant", label: t("Assistente AI"), icon: Sparkles, show: isCompany },
    { to: "/billing", label: t("Abbonamento"), icon: CreditCard, show: isCompany },
  ].filter((n) => n.show);

  const NavItems = () => (
    <>
      {nav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === "/orders"}
          data-testid={`nav-${n.to.replace(/\//g, "-").replace(/^-/, "")}`}
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
              isActive
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`
          }
        >
          <n.icon className="h-[18px] w-[18px]" />
          {n.label}
        </NavLink>
      ))}
    </>
  );

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen flex app-soft-bg overflow-x-hidden">
      <InstallPrompt />
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-white p-4 gap-6 sticky top-0 h-screen">
        <div className="flex items-center gap-2 px-2 pt-2">
          <img src={LOGO} alt={APP_NAME} className="h-9 w-9 rounded-lg" />
          <div>
            <p className="font-display font-extrabold leading-none text-foreground">{APP_NAME}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{t("Gestionale vendite")}</p>
          </div>
        </div>
        <nav className="flex flex-col gap-1">
          <NavItems />
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-border bg-white/90 backdrop-blur px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
            <img src={LOGO} alt={APP_NAME} className="lg:hidden h-8 w-8 rounded-lg" data-testid="mobile-header-logo" />
            <span
              className={`hidden sm:inline text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${
                isCompany ? "bg-sky-100 text-sky-700" : "bg-violet-100 text-violet-700"
              }`}
            >
              {isCompany ? t("Azienda") : t("Agente")}
            </span>

            {/* Company switcher */}
            {companies.length > 0 && (
              <div className="hidden md:flex items-center gap-2 min-w-0">
                {isCompany || companies.length === 1 ? (
                  <span className="flex items-center gap-1.5 text-sm font-medium truncate">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    {companies[0]?.company_name}
                  </span>
                ) : (
                  <Select value={activeCompanyId || ""} onValueChange={setActiveCompany}>
                    <SelectTrigger className="h-9 w-[200px]" data-testid="company-switcher-dropdown">
                      <SelectValue placeholder={t("Scegli azienda")} />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {!isCompany && (
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setJoinOpen(true)} data-testid="join-company-btn" title={t("Collegati a un'azienda")}>
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <span className="hidden md:block"><LanguageSwitcher loggedIn /></span>
            {fs.supported && (
              <Button variant="ghost" size="icon" className="hidden md:flex h-9 w-9" onClick={fs.toggle} data-testid="topbar-fullscreen-btn" title={fs.active ? t("Esci da schermo intero") : t("Schermo intero")}>
                {fs.active ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            )}
            <Button size="sm" onClick={() => navigate("/orders/new")} className="hidden md:flex gap-2 font-semibold" data-testid="topbar-new-order-btn">
              <Plus className="h-4 w-4" /> {t("Ordine")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="h-9 w-9 shrink-0 rounded-full bg-accent text-accent-foreground grid place-items-center text-sm font-bold border border-primary/20">
                  {initials}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 max-w-[calc(100vw-1rem)]">
                <DropdownMenuLabel className="truncate">{user?.name}</DropdownMenuLabel>
                <p className="px-2 -mt-1 pb-1 text-xs text-muted-foreground truncate">{user?.email}</p>
                <DropdownMenuSeparator />
                <MobileMenuExtras isCompany={isCompany} companies={companies} activeCompanyId={activeCompanyId}
                  setActiveCompany={setActiveCompany} onJoin={() => setJoinOpen(true)} fs={fs} />
                {isCompany && (
                  <DropdownMenuItem onClick={() => navigate("/billing")} data-testid="menu-billing">
                    <CreditCard className="h-4 w-4 mr-2" /> {t("Abbonamento")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> {t("Esci")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {mobileOpen && (
          <div className="lg:hidden border-b border-border bg-white p-3 flex flex-col gap-1">
            <NavItems />
          </div>
        )}

        {trialBanner && (
          <div className="bg-amber-50 border-b border-amber-200 text-amber-900 px-4 sm:px-6 py-2.5 text-sm flex items-center justify-between gap-3">
            <span>
              {t("Prova gratuita:")} <b>{sub.trial_days_left} {t("giorni")}</b> {t("rimanenti. Attiva l'abbonamento per non interrompere il servizio.")}
            </span>
            <Button size="sm" variant="outline" className="border-amber-300 bg-white" onClick={() => navigate("/billing")} data-testid="banner-subscribe-btn">
              {t("Abbonati")}
            </Button>
          </div>
        )}

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
      </div>

      <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Collegati a un'azienda")}</DialogTitle>
            <DialogDescription>{t("Inserisci il codice invito ricevuto dall'azienda.")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>{t("Codice invito")}</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("ACME-1A2B3C")} className="font-mono-j uppercase" data-testid="join-code-input" />
          </div>
          <DialogFooter>
            <Button onClick={() => join.mutate()} disabled={!code || join.isPending} data-testid="join-submit-btn">
              {join.isPending ? t("Collegamento…") : t("Collega")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
